import { InvoiceStatusEnum } from './invoice'

/**
 * Invoice lifecycle rules shared by the API (enforcement) and the clients
 * (which actions to offer). Keep the two sides on these helpers so a button
 * is never shown for an action the server refuses.
 *
 * - DRAFT: fully editable; the only status that can be deleted.
 * - SENT / OVERDUE / PARTIALLY_PAID: the client already has the document.
 *   Edits are allowed, but the total can never drop below the amount paid,
 *   and the client only sees changes when the invoice is sent again.
 * - PAID / VOID / CANCELLED: the document is frozen (lines, totals,
 *   currency, client). VOID and CANCELLED are terminal.
 */

/** Statuses whose document (lines, totals, currency, client) is frozen. */
export const INVOICE_READ_ONLY_STATUSES: readonly InvoiceStatusEnum[] = [
  InvoiceStatusEnum.PAID,
  InvoiceStatusEnum.VOID,
  InvoiceStatusEnum.CANCELLED,
]

/** Statuses where the client has already received the document. */
export const INVOICE_DELIVERED_STATUSES: readonly InvoiceStatusEnum[] = [
  InvoiceStatusEnum.SENT,
  InvoiceStatusEnum.OVERDUE,
  InvoiceStatusEnum.PARTIALLY_PAID,
]

/** Terminal statuses: an invoice never leaves them. */
export const INVOICE_TERMINAL_STATUSES: readonly InvoiceStatusEnum[] = [
  InvoiceStatusEnum.VOID,
  InvoiceStatusEnum.CANCELLED,
]

type StatusLike = InvoiceStatusEnum | `${InvoiceStatusEnum}` | string | null

export function isInvoiceReadOnly(status: StatusLike | undefined): boolean {
  return INVOICE_READ_ONLY_STATUSES.includes(status as InvoiceStatusEnum)
}

export function isInvoiceDelivered(status: StatusLike | undefined): boolean {
  return INVOICE_DELIVERED_STATUSES.includes(status as InvoiceStatusEnum)
}

export function isInvoiceTerminal(status: StatusLike | undefined): boolean {
  return INVOICE_TERMINAL_STATUSES.includes(status as InvoiceStatusEnum)
}

/** Only drafts can be deleted; anything the client has seen is voided. */
export function isInvoiceDeletable(status: StatusLike | undefined): boolean {
  return status === InvoiceStatusEnum.DRAFT
}

export type InvoiceEditMode = 'editable' | 'delivered' | 'read-only'

export interface InvoiceEditState {
  mode: InvoiceEditMode
  /** Banner copy for the editor; null for an ordinary draft. */
  message: string | null
}

export function getInvoiceEditState(
  status: StatusLike | undefined,
): InvoiceEditState {
  switch (status) {
    case InvoiceStatusEnum.PAID:
      return {
        mode: 'read-only',
        message:
          'This invoice is paid, so it can no longer be edited. Duplicate it to bill again.',
      }
    case InvoiceStatusEnum.VOID:
      return {
        mode: 'read-only',
        message:
          'This invoice is void, so it can no longer be edited. Duplicate it to bill again.',
      }
    case InvoiceStatusEnum.CANCELLED:
      return {
        mode: 'read-only',
        message:
          'This invoice is canceled, so it can no longer be edited. Duplicate it to bill again.',
      }
    case InvoiceStatusEnum.SENT:
    case InvoiceStatusEnum.OVERDUE:
    case InvoiceStatusEnum.PARTIALLY_PAID:
      return {
        mode: 'delivered',
        message:
          'This invoice was already sent. Changes are saved here, but the client only sees them if you send the invoice again.',
      }
    default:
      return { mode: 'editable', message: null }
  }
}

export type InvoiceVoidBlockReason = 'DRAFT' | 'TERMINAL' | 'HAS_PAYMENTS'

/**
 * Why an invoice cannot be voided, or null when it can. Drafts are deleted
 * instead; an invoice with money applied must have its payments refunded or
 * removed first, so the ledger never shows money on a void document.
 */
export function getInvoiceVoidBlockReason(invoice: {
  status?: StatusLike
  amountPaid?: number | string | null
}): InvoiceVoidBlockReason | null {
  if (invoice.status === InvoiceStatusEnum.DRAFT) return 'DRAFT'
  if (isInvoiceTerminal(invoice.status)) return 'TERMINAL'
  if (Math.round(Number(invoice.amountPaid ?? 0) * 100) > 0) {
    return 'HAS_PAYMENTS'
  }
  return null
}

export const INVOICE_VOID_BLOCK_MESSAGES: Record<
  InvoiceVoidBlockReason,
  string
> = {
  DRAFT: 'Drafts are never sent, so delete the draft instead of voiding it.',
  TERMINAL: 'This invoice is already void or canceled.',
  HAS_PAYMENTS:
    'This invoice has payments applied. Refund or remove payments first.',
}
